import domtoimage from 'dom-to-image-more';

const MIN_SHORT_SIDE = 1080;

function extractBackgroundUrl(node) {
  const inline = node.style.backgroundImage;
  const computed = inline || getComputedStyle(node).backgroundImage;
  const match = computed?.match(/url\(["']?(.*?)["']?\)/);

  return match?.[1] || null;
}

async function fetchImageAsDataUrl(url) {
  const response = await fetch(url, {
    mode: 'cors',
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new Error(
      `Could not load background image for export (HTTP ${response.status}). `
      + 'If this persists, verify Supabase Storage CORS allows your app origin.',
    );
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read image for export.'));
    reader.readAsDataURL(blob);
  });
}

async function inlineRemoteImages(node) {
  const restores = [];

  const backgroundTargets = [
    node,
    node.querySelector('.framed-photo'),
  ].filter(Boolean);

  for (const target of backgroundTargets) {
    const backgroundUrl = extractBackgroundUrl(target);

    if (backgroundUrl && !backgroundUrl.startsWith('data:')) {
      const dataUrl = await fetchImageAsDataUrl(backgroundUrl);
      const previous = target.style.backgroundImage;
      target.style.backgroundImage = `url("${dataUrl}")`;
      restores.push(() => {
        target.style.backgroundImage = previous;
      });
    }
  }

  const logoImg = node.querySelector('.mini-logo img');

  if (logoImg?.src && !logoImg.src.startsWith('data:')) {
    const dataUrl = await fetchImageAsDataUrl(logoImg.src);
    const previousSrc = logoImg.src;
    logoImg.src = dataUrl;
    restores.push(() => {
      logoImg.src = previousSrc;
    });
  }

  return () => {
    restores.reverse().forEach((restore) => restore());
  };
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function slugifyPostFileName(headline) {
  const slug = (headline || 'post')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

  return `${slug || 'post'}.png`;
}

export async function exportPostArtworkPng(
  node,
  { fileName, minShortSide = MIN_SHORT_SIDE } = {},
) {
  if (!node) {
    throw new Error('Nothing to export.');
  }

  await new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

  const rect = node.getBoundingClientRect();
  const shortSide = Math.min(rect.width, rect.height) || 1;
  const pixelRatio = Math.max(2, minShortSide / shortSide);

  let restoreImages = () => {};

  try {
    restoreImages = await inlineRemoteImages(node);

    const blob = await domtoimage.toBlob(node, {
      pixelRatio,
      cacheBust: true,
      fetchRequestInit: {
        mode: 'cors',
        credentials: 'omit',
      },
    });

    if (!blob) {
      throw new Error('PNG export failed — no image data was produced.');
    }

    downloadBlob(blob, fileName || slugifyPostFileName());
  } catch (error) {
    if (error instanceof TypeError && /fetch/i.test(error.message)) {
      throw new Error(
        'Export blocked by CORS while loading the background image. '
        + 'Ensure Supabase Storage serves images with Access-Control-Allow-Origin for this app.',
      );
    }

    throw error instanceof Error
      ? error
      : new Error('PNG export failed.');
  } finally {
    restoreImages();
  }
}
